"use client";

import * as React from "react";
import { signIn } from "next-auth/react";
import { toast } from "sonner";
import {
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Smartphone,
  Ticket,
  User,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { isValidPhone } from "@/lib/format";
import { pushView, useAppStore } from "@/stores/useAppStore";

type Touched = {
  name?: boolean;
  phone?: boolean;
  password?: boolean;
  confirm?: boolean;
};

export default function RegisterForm() {
  // Read captured referral code once on mount (from ?ref= URL param).
  const initialRef = useAppStore.getState().capturedRef ?? "";
  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [refCode, setRefCode] = React.useState(initialRef);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [touched, setTouched] = React.useState<Touched>({});

  const nameError = touched.name && name.trim().length < 2;
  const phoneError = touched.phone && !isValidPhone(phone);
  const passwordError = touched.password && password.length < 6;
  const confirmError = touched.confirm && confirm !== password;

  function blur<K extends keyof Touched>(field: K) {
    setTouched((t) => ({ ...t, [field]: true }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched({
      name: true,
      phone: true,
      password: true,
      confirm: true,
    });

    if (name.trim().length < 2) {
      toast.error("نام و نام خانوادگی را وارد کنید");
      return;
    }
    if (!isValidPhone(phone)) {
      toast.error("شماره موبایل معتبر نیست");
      return;
    }
    if (password.length < 6) {
      toast.error("رمز عبور باید حداقل ۶ کاراکتر باشد");
      return;
    }
    if (confirm !== password) {
      toast.error("تکرار رمز عبور مطابقت ندارد");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone,
          name: name.trim(),
          password,
          refCode: refCode.trim() ? refCode.trim().toUpperCase() : undefined,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        ok?: boolean;
        vpnAccess?: boolean;
      };

      if (!res.ok || data.error) {
        toast.error(data.error || "خطا در ثبت‌نام. دوباره تلاش کنید.");
        return;
      }

      if (data.vpnAccess === true) {
        toast.success("دسترسی ویژه فعال شد! خوش آمدید.");
      } else {
        toast.success("ثبت‌نام موفقیت‌آمیز بود!");
      }

      // Sign in and let NextAuth redirect to the dashboard view.
      await signIn("credentials", {
        phone,
        password,
        redirect: true,
        callbackUrl: "/?view=dashboard",
      });
    } catch (err) {
      console.error(err);
      toast.error("خطایی رخ داد. دوباره تلاش کنید.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className="w-full max-w-md rounded-2xl border-border/60 shadow-lg">
      <CardHeader className="gap-2">
        <div className="mb-1 flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => pushView("landing")}
          >
            <ArrowRight className="size-4" />
            بازگشت به خانه
          </Button>
        </div>
        <CardTitle className="text-2xl font-bold tracking-tight">
          ساخت حساب کاربری
        </CardTitle>
        <CardDescription>در کمتر از یک دقیقه ثبت‌نام کنید</CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          <div className="flex flex-col gap-2">
            <Label htmlFor="register-name">نام و نام خانوادگی</Label>
            <div className="relative">
              <User className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="register-name"
                name="name"
                type="text"
                autoComplete="name"
                placeholder="مثال: علی رضایی"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => blur("name")}
                aria-invalid={nameError}
                aria-describedby={
                  nameError ? "register-name-error" : undefined
                }
                disabled={loading}
                className="h-11 rounded-xl pr-10 text-base md:text-base"
              />
            </div>
            {nameError && (
              <p
                id="register-name-error"
                className="text-xs text-destructive"
                role="alert"
              >
                نام باید حداقل ۲ کاراکتر باشد
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="register-phone">شماره موبایل</Label>
            <div className="relative">
              <Smartphone className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="register-phone"
                name="phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                dir="ltr"
                placeholder="09121111111"
                value={phone}
                onChange={(e) =>
                  setPhone(e.target.value.replace(/[^\d]/g, ""))
                }
                onBlur={() => blur("phone")}
                aria-invalid={phoneError}
                aria-describedby={
                  phoneError ? "register-phone-error" : undefined
                }
                disabled={loading}
                className="h-11 rounded-xl pr-10 text-left text-base md:text-base"
              />
            </div>
            {phoneError && (
              <p
                id="register-phone-error"
                className="text-xs text-destructive"
                role="alert"
              >
                شماره موبایل معتبر نیست
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="register-password">رمز عبور</Label>
            <div className="relative">
              <Lock className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="register-password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                dir="ltr"
                placeholder="حداقل ۶ کاراکتر"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => blur("password")}
                aria-invalid={passwordError}
                aria-describedby={
                  passwordError ? "register-password-error" : undefined
                }
                disabled={loading}
                className="h-11 rounded-xl pr-10 pl-10 text-left text-base md:text-base"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={showPassword ? "پنهان کردن رمز" : "نمایش رمز"}
                className="absolute left-1 top-1/2 size-8 -translate-y-1/2 text-muted-foreground hover:bg-transparent hover:text-foreground"
                onClick={() => setShowPassword((s) => !s)}
                tabIndex={-1}
                disabled={loading}
              >
                {showPassword ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </Button>
            </div>
            {passwordError && (
              <p
                id="register-password-error"
                className="text-xs text-destructive"
                role="alert"
              >
                رمز عبور باید حداقل ۶ کاراکتر باشد
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="register-confirm">تکرار رمز عبور</Label>
            <div className="relative">
              <Lock className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="register-confirm"
                name="confirm"
                type={showConfirm ? "text" : "password"}
                autoComplete="new-password"
                dir="ltr"
                placeholder="••••••••"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                onBlur={() => blur("confirm")}
                aria-invalid={confirmError}
                aria-describedby={
                  confirmError ? "register-confirm-error" : undefined
                }
                disabled={loading}
                className="h-11 rounded-xl pr-10 pl-10 text-left text-base md:text-base"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={showConfirm ? "پنهان کردن رمز" : "نمایش رمز"}
                className="absolute left-1 top-1/2 size-8 -translate-y-1/2 text-muted-foreground hover:bg-transparent hover:text-foreground"
                onClick={() => setShowConfirm((s) => !s)}
                tabIndex={-1}
                disabled={loading}
              >
                {showConfirm ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </Button>
            </div>
            {confirmError && (
              <p
                id="register-confirm-error"
                className="text-xs text-destructive"
                role="alert"
              >
                تکرار رمز عبور مطابقت ندارد
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="register-ref">کد معرف (اختیاری)</Label>
            <div className="relative">
              <Ticket className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="register-ref"
                name="refCode"
                type="text"
                autoComplete="off"
                dir="ltr"
                placeholder="مثال: REF1234"
                value={refCode}
                onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                disabled={loading}
                className="h-11 rounded-xl pr-10 text-left text-base md:text-base"
                maxLength={32}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              اگر کد معرف دارید وارد کنید.
            </p>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="h-11 w-full rounded-xl text-base font-semibold"
          >
            {loading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                در حال ثبت‌نام...
              </>
            ) : (
              "ساخت حساب"
            )}
          </Button>
        </form>
      </CardContent>

      <CardFooter className="justify-center">
        <p className="text-sm text-muted-foreground">
          حساب دارید؟{" "}
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-primary"
            onClick={() => pushView("login")}
            disabled={loading}
          >
            وارد شوید
          </Button>
        </p>
      </CardFooter>
    </Card>
  );
}
